import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import WelcomeVideo from "./WelcomeVideo";
import ProfileSettings from "./ProfileSettings";
import Sidebar from "./Sidebar";
import IconeEscola from '../assets/iconeescola.png';
import {
  BookOpen,
  Award,
  Clock,
  TrendingUp,
  User,
  LogOut,
  PlayCircle,
  CheckCircle2,
  Target,
  Settings,
  Lock,
  Search,
  Bell,
  X,
  ExternalLink,
  Menu
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [certificates, setCertificates] = useState([]);
  const [trails, setTrails] = useState([]);
  const [settings, setSettings] = useState({});
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showCertificatesModal, setShowCertificatesModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const coursesRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchDashboardData();
    }
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch courses
      const coursesResponse = await axios.get(`${API}/courses`, { headers });
      setCourses(coursesResponse.data);
      
      // Fetch settings
      const settingsResponse = await axios.get(`${API}/settings`, { headers });
      setSettings(settingsResponse.data);
      
      // Show welcome video only if user hasn't seen it yet
      if (settingsResponse.data.show_welcome_video && settingsResponse.data.welcome_video_url) {
        const jaViu = localStorage.getItem(`welcomeVideo_${user.id}`);
        if (!jaViu) {
          setShowWelcomeVideo(true);
        }
      }
      
      // Fetch progress for each course
      const progressPromises = coursesResponse.data.map(async (course) => {
        try {
          const progressResponse = await axios.get(`${API}/progress/${course.id}`, { headers });
          return { [course.id]: progressResponse.data };
        } catch (error) {
          console.error(`Error fetching progress for course ${course.id}:`, error);
          return { [course.id]: { completion_percentage: 0, is_completed: false } };
        }
      });
      
      const progressResults = await Promise.all(progressPromises);
      const progressMap = progressResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setProgress(progressMap);
      
      // Fetch certificates — API returns { course_certificates: [], assessment_certificates: [] }
      const certificatesResponse = await axios.get(`${API}/certificates`, { headers });
      const certData = certificatesResponse.data || {};
      const courseCerts = (certData.course_certificates || []).map(c => ({ ...c, type: 'course' }));
      const assessmentCerts = (certData.assessment_certificates || []).map(c => ({ ...c, type: 'assessment' }));
      setCertificates([...courseCerts, ...assessmentCerts]);

      // Fetch user trails
      const trailsResponse = await axios.get(`${API}/user/trails`, { headers });
      const userTrailsData = trailsResponse.data || [];
      setTrails(userTrailsData);

      const orderedCourses = sortCoursesByTrailOrder(coursesResponse.data, userTrailsData);
      setCourses(orderedCourses);
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeVideoClose = () => {
    setShowWelcomeVideo(false);
    // Mark as seen for this user
    localStorage.setItem(`welcomeVideo_${user.id}`, 'true');
  };

  const handleProfileUpdate = async (updatedUser) => {
    // Atualizar contexto do usuário se necessário
    // O componente pai (App.js) pode precisar atualizar o contexto
    // Por ora, apenas recarregar os dados do dashboard
    await fetchDashboardData();
  };

  const getSectorDisplayName = (sector) => {
    const sectorNames = {
      'estagio': 'Estágio',
      'tecnico': 'Técnico',
      'comercial': 'Comercial'
    };
    return sectorNames[sector] || sector;
  };

  const downloadCertificate = async (courseId, courseTitle) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/certificates/course/${courseId}/download`, {
        headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificado_${courseTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success("Certificado baixado com sucesso!");
    } catch (error) {
      console.error("Error downloading certificate:", error);
      toast.error("Erro ao baixar certificado");
    }
  };

  const downloadAssessmentCertificate = async (assessmentId, assessmentTitle) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/certificates/assessment/${assessmentId}/download`, {
        headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificado_${(assessmentTitle || 'Avaliacao').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success("Certificado baixado com sucesso!");
    } catch (error) {
      console.error("Error downloading assessment certificate:", error);
      toast.error("Erro ao baixar certificado");
    }
  };

  const getCompletedCoursesCount = () => {
    return Object.values(progress).filter(p => p.is_completed).length;
  };

  const getAverageProgress = () => {
    if (Object.keys(progress).length === 0) return 0;
    const totalProgress = Object.values(progress).reduce((sum, p) => sum + p.completion_percentage, 0);
    return Math.round(totalProgress / Object.keys(progress).length);
  };

  const sortCoursesByTrailOrder = (courseList, userTrailsData) => {
    if (!Array.isArray(courseList)) return [];
    const orderMap = new Map();
    (userTrailsData || []).forEach((trail, trailIndex) => {
      (trail.course_ids || []).forEach((courseId, courseIndex) => {
        if (!orderMap.has(courseId)) {
          orderMap.set(courseId, trailIndex * 1000 + courseIndex);
        }
      });
    });

    return [...courseList].sort((a, b) => {
      const orderA = orderMap.has(a.id) ? orderMap.get(a.id) : null;
      const orderB = orderMap.has(b.id) ? orderMap.get(b.id) : null;

      if (orderA !== null && orderB !== null) {
        return orderA - orderB;
      }
      if (orderA !== null) return -1;
      if (orderB !== null) return 1;

      const trailOrderA = typeof a.trail_order === "number" ? a.trail_order : 9999;
      const trailOrderB = typeof b.trail_order === "number" ? b.trail_order : 9999;

      if (trailOrderA !== trailOrderB) {
        return trailOrderA - trailOrderB;
      }

      return (a.title || "").localeCompare(b.title || "");
    });
  };

  const CARD_WIDTH = 280;

  const renderCourseCard = (course) => {
    const wrapperClass = "h-full flex-shrink-0";
    if (!course) return null;
    const courseProgress = progress[course.id] || { completion_percentage: 0, is_completed: false };
    const accessInfo = course.access_info || { can_access: true, reason: "No restriction" };
    const isBlocked = !accessInfo.can_access;

    const handleCourseClick = (e) => {
      if (isBlocked) {
        e.preventDefault();
        const message = accessInfo.reason || "Curso ainda bloqueado, complete o anterior com êxito.";
        toast.error(message);
      }
    };

    return (
      <div key={course.id} className={`${wrapperClass}`} style={{ width: `${CARD_WIDTH}px` }}>
      <Link
        to={isBlocked ? '#' : `/course/${course.id}`}
        className={`netflix-card-link block h-full ${isBlocked ? 'opacity-50 pointer-events-none' : ''}`}
        data-testid={`course-card-${course.id}`}
        onClick={handleCourseClick}
      >
        <div className="netflix-card group h-full bg-[#0a1224] border border-blue-900/40 text-white">
          <div className="netflix-thumbnail">
            {course.thumbnail_url ? (
              <img
                src={course.thumbnail_url}
                alt={course.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600">
                <BookOpen className="w-12 h-12 text-white opacity-80" />
              </div>
            )}

            {isBlocked && (
              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="text-2xl mb-2">🔒</div>
                  <p className="text-sm">Bloqueado</p>
                </div>
              </div>
            )}

            {courseProgress.completion_percentage > 0 && !isBlocked && (
              <div className="netflix-progress-overlay">
                <div
                  className="netflix-progress-bar"
                  style={{ width: `${courseProgress.completion_percentage}%` }}
                ></div>
              </div>
            )}

            {courseProgress.is_completed && !isBlocked && (
              <div className="absolute top-3 right-3">
                <div className="bg-green-500 rounded-full p-1">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
              </div>
            )}

            {!isBlocked && (
              <div className="netflix-hover-play">
                <PlayCircle className="w-16 h-16 text-white" />
              </div>
            )}
          </div>

          <div className="netflix-card-info">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="netflix-match">
                  {Math.round(courseProgress.completion_percentage || 0)}% assistido
                </span>
                {courseProgress.is_completed && (
                  <Badge className="netflix-badge-completed">
                    Concluído
                  </Badge>
                )}
              </div>
              <span className="netflix-duration">
                {course.total_duration_minutes}m
              </span>
            </div>

            <h5 className="netflix-card-title">
              {course.title}
            </h5>

            <p className="netflix-card-description">
              {course.description}
            </p>

            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-4 text-gray-400 text-sm">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4 text-blue-400" />
                  {course.segments?.length || 0} módulos
                </span>
                <span className="bg-gray-700 px-2 py-0.5 rounded text-xs">
                  {getSectorDisplayName(user.sector)}
                </span>
              </div>

              {courseProgress.is_completed && !isBlocked && (
                (!course.requires_assessment || courseProgress.assessment_passed) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      downloadCertificate(course.id, course.title);
                    }}
                    className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black text-xs px-2 py-1"
                    data-testid={`download-certificate-${course.id}`}
                  >
                    <Award className="w-3 h-3 mr-1" />
                    Certificado
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </Link>
      </div>
    );
  };

  const renderFinalAssessmentCard = (trail) => {
    if (!trail || !trail.requires_assessment || !trail.linked_assessment_id) return null;
    const wrapperClass = "h-full flex-shrink-0";
    const isCompleted = trail.assessment_passed;
    const isUnlocked = trail.can_take_final_assessment || isCompleted;
    const lockedReason = trail.final_assessment_locked_reason || "Conclua todos os cursos da trilha para liberar a avaliação final.";

    const handleAssessmentClick = (e) => {
      if (!isUnlocked) {
        e.preventDefault();
        toast.error(lockedReason);
      }
    };

    return (
      <div key={`trail-final-${trail.id}`} className={wrapperClass} style={{ width: `${CARD_WIDTH}px` }}>
      <Link
        to={`/assessment/${trail.linked_assessment_id}`}
        className="netflix-card-link block h-full"
        onClick={handleAssessmentClick}
        data-testid={`trail-final-card-${trail.id}`}
      >
        <div className="netflix-card group border border-blue-900/50 h-full">
          <div className="netflix-thumbnail flex flex-col justify-between bg-gradient-to-br from-blue-700 via-purple-700 to-pink-600 p-4">
            <div className="flex items-center gap-2 text-white">
              <Target className="w-6 h-6" />
              <span className="uppercase tracking-wide text-xs font-semibold">Avaliação Final</span>
            </div>
            <div className="text-white">
              <p className="text-lg font-semibold mb-1">{trail.name}</p>
              <p className="text-sm text-white/80">Trilha obrigatória do seu setor</p>
            </div>
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2 text-sm">
                {isCompleted ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-300" />
                    <span>Concluída</span>
                  </>
                ) : isUnlocked ? (
                  <>
                    <PlayCircle className="w-4 h-4" />
                    <span>Liberada</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Bloqueada</span>
                  </>
                )}
              </div>
              <span className="text-xs bg-white/20 px-2 py-1 rounded">
                {trail.completed_courses}/{trail.total_courses} cursos
              </span>
            </div>
          </div>

          <div className="netflix-card-info">
            <div className="flex items-center justify-between mb-2">
              <span className="netflix-match text-green-400">
                {isCompleted ? "Avaliação concluída" : isUnlocked ? "Pronta para iniciar" : "Complete os cursos"}
              </span>
              <span className="netflix-duration text-blue-300">Trilha</span>
            </div>

            <h5 className="netflix-card-title">
              Avaliação final da trilha
            </h5>

            <p className="netflix-card-description">
              {isCompleted
                ? "Parabéns! Você finalizou esta avaliação. Clique para revisar o resultado."
                : isUnlocked
                  ? "Finalize sua jornada respondendo à avaliação final para liberar o certificado."
                  : lockedReason}
            </p>

            <div className="flex items-center justify-between mt-3 text-sm">
              <div className="text-gray-400">
                {isCompleted
                  ? "Certificado liberado"
                  : isUnlocked
                    ? "A avaliação está disponível"
                    : "Faltam cursos para liberar"}
              </div>
              <div className="flex items-center gap-2 text-blue-400">
                <Target className="w-4 h-4" />
                <span>{isCompleted ? "Ver resultado" : "Acessar"}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
      </div>
    );
  };

  const trailSections = useMemo(() => {
    if (!Array.isArray(trails) || trails.length === 0) return [];
    const courseMap = new Map(courses.map(course => [course.id, course]));

    return trails.map(trail => ({
      trail,
      courses: (trail.course_ids || []).map(courseId => courseMap.get(courseId)).filter(Boolean)
    }));
  }, [trails, courses]);

  const assignedCourseIds = useMemo(() => {
    const ids = new Set();
    trailSections.forEach(section => {
      section.courses.forEach(course => ids.add(course.id));
    });
    return ids;
  }, [trailSections]);

  const standaloneCourses = useMemo(() => {
    return courses.filter(course => !assignedCourseIds.has(course.id));
  }, [courses, assignedCourseIds]);

  const filteredTrailSections = useMemo(() => {
    if (!searchQuery.trim()) return trailSections;
    const q = searchQuery.toLowerCase();
    return trailSections.map(section => ({
      ...section,
      courses: section.courses.filter(c => c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q))
    })).filter(section => section.courses.length > 0);
  }, [trailSections, searchQuery]);

  const filteredStandaloneCourses = useMemo(() => {
    if (!searchQuery.trim()) return standaloneCourses;
    const q = searchQuery.toLowerCase();
    return standaloneCourses.filter(c => c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
  }, [standaloneCourses, searchQuery]);

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ width: 40, height: 40, borderColor: '#818cf8', margin: '0 auto 1rem' }}></div>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', color: '#4a4a6a' }}>Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <Sidebar
        user={user}
        onLogout={handleLogout}
        onOpenProfile={() => setShowProfileSettings(true)}
        onShowCertificates={() => setShowCertificatesModal(true)}
        onShowMyCourses={() => coursesRef.current?.scrollIntoView({ behavior: 'smooth' })}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(v => !v)}
      />

      <div className="dashboard-content" style={{ background: '#0a0a0a' }}>
      {/* Welcome Video Modal */}
      <WelcomeVideo
        settings={settings}
        isOpen={showWelcomeVideo}
        onClose={handleWelcomeVideoClose}
        onComplete={handleWelcomeVideoClose}
      />

      {/* Profile Settings Modal */}
      <ProfileSettings
        open={showProfileSettings}
        onOpenChange={setShowProfileSettings}
        token={token}
        user={user}
        onProfileUpdate={handleProfileUpdate}
      />

      {/* Certificates Modal */}
      {showCertificatesModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowCertificatesModal(false)}
        >
          <div
            style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', width: '100%', maxWidth: 640, margin: '0 1rem', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Award size={14} style={{ color: '#eab308' }} />
                </div>
                <div>
                  <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0', margin: 0 }}>Meus Certificados</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#4a4a6a', margin: 0 }}>{certificates.length} certificado{certificates.length !== 1 ? 's' : ''} conquistado{certificates.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button
                onClick={() => setShowCertificatesModal(false)}
                style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#5a5a7a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={14} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {certificates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                  <Award size={48} style={{ color: '#2a2a3a', margin: '0 auto 1rem' }} />
                  <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.9rem', fontWeight: 600, color: '#4a4a6a', margin: '0 0 0.25rem' }}>Nenhum certificado ainda</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#3a3a5a', margin: 0 }}>Conclua cursos e avaliações para ganhar certificados.</p>
                </div>
              ) : (
                certificates.map((certificate) => {
                  const isCourse = certificate.type === 'course';
                  const title = isCourse
                    ? (certificate.course_title || 'Certificado de Curso')
                    : (certificate.assessment_title || 'Certificado de Avaliação');
                  const badgeLabel = isCourse ? 'Curso' : 'Avaliação';
                  const badgeColor = isCourse
                    ? { bg: 'rgba(96,165,250,0.1)', color: '#60a5fa', border: 'rgba(96,165,250,0.2)' }
                    : { bg: 'rgba(168,85,247,0.1)', color: '#c084fc', border: 'rgba(168,85,247,0.2)' };
                  return (
                    <div key={certificate.id} className="admin-list-row" style={{ justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Award size={16} style={{ color: '#eab308' }} />
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 2 }}>
                            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#d8d8e8', margin: 0 }}>{title}</p>
                            <span className="admin-badge" style={{ background: badgeColor.bg, color: badgeColor.color, border: `1px solid ${badgeColor.border}` }}>{badgeLabel}</span>
                          </div>
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: 0 }}>
                            Emitido em {new Date(certificate.issued_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => isCourse
                          ? downloadCertificate(certificate.course_id, title)
                          : downloadAssessmentCertificate(certificate.assessment_id, title)
                        }
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.38rem 0.85rem', borderRadius: 7, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)', color: '#eab308', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif", flexShrink: 0 }}
                      >
                        <Award size={12} />
                        Baixar
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header style={{ background: '#0d0d14', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 1.5rem', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img
              src={IconeEscola}
              alt="Escola de Suporti"
              style={{ height: 36, width: 36, objectFit: 'contain' }}
            />
            <div>
              <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: '#e8e8f0', margin: 0 }}>Escola de Suporti</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.68rem', color: '#3a3a5a', margin: 0 }}>Plataforma de Treinamentos</p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* User info — hidden on mobile */}
            <div className="hidden md:block" style={{ textAlign: 'right' }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#d8d8e8', margin: 0 }}>{user.name}</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.68rem', color: '#3a3a5a', margin: 0 }}>
                {getSectorDisplayName(user.sector)} · {user.role}
              </p>
            </div>

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(v => !v)}
                data-testid="notifications-btn"
                style={{ position: 'relative', width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#8a8ab0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Bell size={15} />
                {(() => {
                  const count = Math.min(
                    (trails.filter(t => t.can_take_final_assessment && !t.assessment_passed).length) +
                    (certificates.length === 0 && Object.values(progress).some(p => p.is_completed) ? 1 : 0),
                    9
                  );
                  return count ? (
                    <span style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: '#fff', fontSize: '0.6rem', fontWeight: 700, borderRadius: '50%', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>{count}</span>
                  ) : null;
                })()}
              </button>

              {showNotifications && (
                <div style={{ position: 'absolute', right: 0, top: 40, width: 300, background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 50 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.82rem', fontWeight: 700, color: '#e8e8f0' }}>Notificações</span>
                    <button onClick={() => setShowNotifications(false)} style={{ background: 'none', border: 'none', color: '#5a5a7a', cursor: 'pointer', display: 'flex' }}>
                      <X size={14} />
                    </button>
                  </div>
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {trails.filter(t => t.can_take_final_assessment && !t.assessment_passed).map(t => (
                      <div key={t.id} style={{ padding: '0.65rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: '#d8d8e8', margin: '0 0 2px' }}>Avaliação disponível</p>
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#4a4a6a', margin: 0 }}>Você concluiu a trilha <strong style={{ color: '#8a8ab0' }}>{t.name}</strong>. Faça a avaliação final.</p>
                      </div>
                    ))}
                    {certificates.length === 0 && Object.values(progress).some(p => p.is_completed) && (
                      <div style={{ padding: '0.65rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: '#d8d8e8', margin: '0 0 2px' }}>Certificado pendente</p>
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#4a4a6a', margin: 0 }}>Você concluiu cursos mas ainda não tem certificados. Verifique se há avaliações pendentes.</p>
                      </div>
                    )}
                    {courses.filter(c => { const p = progress[c.id]; return !p || p.completion_percentage === 0; }).slice(0, 2).map(c => (
                      <div key={c.id} style={{ padding: '0.65rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: '#d8d8e8', margin: '0 0 2px' }}>Novo curso disponível</p>
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#4a4a6a', margin: 0 }}><strong style={{ color: '#8a8ab0' }}>{c.title}</strong> está disponível para seu setor.</p>
                      </div>
                    ))}
                    {trails.filter(t => t.can_take_final_assessment && !t.assessment_passed).length === 0 &&
                     !(certificates.length === 0 && Object.values(progress).some(p => p.is_completed)) &&
                     courses.filter(c => { const p = progress[c.id]; return !p || p.completion_percentage === 0; }).length === 0 && (
                      <div style={{ padding: '2rem 1rem', textAlign: 'center', fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#3a3a5a' }}>
                        Nenhuma notificação no momento.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Desktop navigation buttons */}
            <div className="hidden md:flex items-center" style={{ gap: '0.5rem' }}>
              <button
                onClick={() => setShowProfileSettings(true)}
                data-testid="profile-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.38rem 0.85rem', borderRadius: 7, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', color: '#818cf8', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              >
                <Settings size={13} />
                Perfil
              </button>

              <Link to="/assessments">
                <button
                  data-testid="assessments-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.38rem 0.85rem', borderRadius: 7, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', color: '#c084fc', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                >
                  <Target size={13} />
                  Avaliações
                </button>
              </Link>

              {(user.role === "admin" || user.role === "coordinator") && (
                <Link to="/admin">
                  <button
                    data-testid="admin-panel-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.38rem 0.85rem', borderRadius: 7, background: 'rgba(20,184,166,0.1)', border: '1px solid rgba(20,184,166,0.2)', color: '#2dd4bf', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                  >
                    <User size={13} />
                    Painel Admin
                  </button>
                </Link>
              )}

              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.38rem 0.85rem', borderRadius: 7, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.16)', color: '#f87171', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              >
                <LogOut size={13} />
                Sair
              </button>
            </div>

            {/* Hamburger button — mobile only */}
            <div className="relative md:hidden">
              <button
                onClick={() => setMobileMenuOpen(v => !v)}
                style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#8a8ab0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
              </button>

              {mobileMenuOpen && (
                <div style={{ position: 'absolute', right: 0, top: 40, width: 220, background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 50, overflow: 'hidden' }}>
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#d8d8e8', margin: 0 }}>{user.name}</p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: 0 }}>{getSectorDisplayName(user.sector)}</p>
                  </div>
                  <button
                    onClick={() => { setShowProfileSettings(true); setMobileMenuOpen(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 1rem', fontFamily: "'Inter', sans-serif", fontSize: '0.8rem', color: '#c0c0d8', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <Settings size={14} /> Perfil
                  </button>
                  <Link to="/assessments" onClick={() => setMobileMenuOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 1rem', fontFamily: "'Inter', sans-serif", fontSize: '0.8rem', color: '#c0c0d8', textDecoration: 'none' }}>
                    <Target size={14} /> Avaliações
                  </Link>
                  {(user.role === "admin" || user.role === "coordinator") && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 1rem', fontFamily: "'Inter', sans-serif", fontSize: '0.8rem', color: '#c0c0d8', textDecoration: 'none' }}>
                      <User size={14} /> Painel Admin
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 1rem', fontFamily: "'Inter', sans-serif", fontSize: '0.8rem', color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <LogOut size={14} /> Sair
                  </button>
                </div>
              )}
            </div>
          </div>
      </header>

      {/* Main Content */}
      <main style={{ padding: '1.5rem', flex: 1 }}>
        {/* Welcome Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700, color: '#e8e8f0', margin: '0 0 0.35rem' }}>
            Bem-vindo, {user.name.split(' ')[0]}!
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', color: '#6a6a9a', margin: 0 }}>
            Continue seu desenvolvimento profissional · Setor <span style={{ color: '#c0c0d8', fontWeight: 500 }}>{getSectorDisplayName(user.sector)}</span>
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.875rem', marginBottom: '1.5rem' }}>
          <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Disponíveis</span>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BookOpen size={13} style={{ color: '#60a5fa' }} />
              </div>
            </div>
            <div>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{courses.length}</span>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Cursos para seu setor</p>
            </div>
          </div>

          <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Concluídos</span>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle2 size={13} style={{ color: '#4ade80' }} />
              </div>
            </div>
            <div>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{getCompletedCoursesCount()}</span>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Cursos finalizados</p>
            </div>
          </div>

          <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Progresso</span>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(230,126,34,0.1)', border: '1px solid rgba(230,126,34,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp size={13} style={{ color: '#E67E22' }} />
              </div>
            </div>
            <div>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{getAverageProgress()}%</span>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Média geral</p>
            </div>
          </div>

          <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Certificados</span>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Award size={13} style={{ color: '#eab308' }} />
              </div>
            </div>
            <div>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{certificates.length}</span>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>{certificates.length === 0 ? 'Nenhum ainda' : `${certificates.length} conquistado${certificates.length > 1 ? 's' : ''}`}</p>
            </div>
          </div>
        </div>

        {/* Courses Section */}
        <div ref={coursesRef}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BookOpen size={14} style={{ color: '#60a5fa' }} />
                </div>
                <div>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.08rem', fontWeight: 700, color: '#e8e8f0', margin: 0 }}>Seus Cursos</h3>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.72rem', color: '#4a4a6a', margin: 0, paddingLeft: '2.55rem', marginTop: '-1.2rem', paddingTop: '1.2rem' }}>Setor {getSectorDisplayName(user.sector)} · {courses.length} curso{courses.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#4a4a6a', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Buscar curso..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  data-testid="course-search-input"
                  style={{ paddingLeft: 30, paddingRight: 12, paddingTop: 7, paddingBottom: 7, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#c0c0d8', fontSize: '0.78rem', fontFamily: "'Inter', sans-serif", outline: 'none', width: 220 }}
                />
              </div>
            </div>
          </div>

          {courses.length === 0 ? (
            <div style={{ padding: '4rem 0', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <BookOpen size={28} style={{ color: '#2a2a3a' }} />
              </div>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#4a4a6a', margin: '0 0 0.35rem' }}>
                Nenhum curso disponível
              </h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.78rem', color: '#3a3a5a', margin: 0 }}>
                No momento não há cursos disponíveis para seu setor. Entre em contato com o administrador.
              </p>
            </div>
          ) : (
            <>
              {/* Featured Course */}
              {courses.length > 0 && (
                <div data-testid="featured-course" style={{ position: 'relative', overflow: 'hidden', borderRadius: 14, background: 'linear-gradient(135deg, #0f1a2e 0%, #1a1035 50%, #200d2a 100%)', border: '1px solid rgba(255,255,255,0.07)', marginBottom: '1.5rem', minHeight: 220 }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 100%)' }}></div>
                  <div style={{ position: 'relative', zIndex: 1, padding: '2rem 2.5rem', display: 'flex', alignItems: 'center', minHeight: 220 }}>
                    <div style={{ maxWidth: 560 }}>
                      <span style={{ display: 'inline-block', fontFamily: "'Inter', sans-serif", fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', padding: '0.25rem 0.6rem', borderRadius: 4, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.35)', color: '#818cf8', marginBottom: '0.75rem' }}>
                        Destaque
                      </span>
                      <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.5rem', fontWeight: 700, color: '#e8e8f0', margin: '0 0 0.5rem', lineHeight: 1.2 }}>
                        {courses[0].title}
                      </h2>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', color: '#8a8ab0', margin: '0 0 1rem', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {courses[0].description}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#5a5a7a' }}>
                          <Clock size={13} style={{ color: '#4a4a6a' }} />
                          {courses[0].total_duration_minutes} min
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#5a5a7a' }}>
                          <BookOpen size={13} style={{ color: '#4a4a6a' }} />
                          {courses[0].segments?.length || 0} módulos
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                        <Link to={`/course/${courses[0].id}`}>
                          <button data-testid="featured-course-watch-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', padding: '0.5rem 1.1rem', borderRadius: 8, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.35)', color: '#818cf8', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                            <PlayCircle size={15} />
                            Assistir
                          </button>
                        </Link>
                        {courses[0].external_url && (
                          <a href={courses[0].external_url} target="_blank" rel="noopener noreferrer">
                            <button data-testid="featured-course-external-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', padding: '0.5rem 1.1rem', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#8a8ab0', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                              <ExternalLink size={13} />
                              YouTube
                            </button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Course Grid */}
              <div className="netflix-section" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.82rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 0.5rem' }}>Todos os Cursos</h4>

                {filteredTrailSections.length === 0 && filteredStandaloneCourses.length === 0 && (
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.78rem', color: '#3a3a5a', padding: '2rem 0', textAlign: 'center' }}>
                    {searchQuery ? `Nenhum curso encontrado para "${searchQuery}".` : 'Nenhuma trilha configurada.'}
                  </div>
                )}

                {filteredTrailSections.map(({ trail, courses: trailCourses }) => {
                  return (
                    <div key={trail.id} style={{ marginTop: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div>
                          <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#d8d8e8', margin: 0 }}>{trail.name}</h4>
                          {trail.description && <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.73rem', color: '#4a4a6a', margin: '2px 0 0' }}>{trail.description}</p>}
                        </div>
                        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.65rem', borderRadius: 6, background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.16)', color: '#60a5fa' }}>
                          {trail.completed_courses}/{trail.total_courses} cursos
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-6">
                        {trailCourses.length > 0 ? (
                          trailCourses.map(course => renderCourseCard(course))
                        ) : (
                          <div className="text-sm text-gray-400">Nenhum curso disponível nesta trilha.</div>
                        )}
                        {!searchQuery && renderFinalAssessmentCard(trail)}
                      </div>
                    </div>
                  );
                })}

                {filteredStandaloneCourses.length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
                      <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#d8d8e8', margin: 0 }}>Outros Cursos</h4>
                      <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.65rem', borderRadius: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#5a5a7a' }}>
                        {filteredStandaloneCourses.length} curso{filteredStandaloneCourses.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-6">
                      {filteredStandaloneCourses.map(course => renderCourseCard(course))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Certificates Section */}
        {certificates.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.875rem' }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Award size={14} style={{ color: '#eab308' }} />
              </div>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.08rem', fontWeight: 700, color: '#e8e8f0', margin: 0 }}>Seus Certificados</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              {certificates.map((certificate) => {
                const isCourse = certificate.type === 'course';
                const title = isCourse
                  ? (certificate.course_title || 'Certificado de Curso')
                  : (certificate.assessment_title || 'Certificado de Avaliação');
                return (
                  <div key={certificate.id} style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Award size={18} style={{ color: '#eab308' }} />
                    </div>
                    <div>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.8rem', fontWeight: 600, color: '#d8d8e8', margin: 0 }}>{title}</p>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.68rem', color: '#3a3a5a', margin: '2px 0 0' }}>
                        {new Date(certificate.issued_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
      </div>  {/* fim dashboard-content */}
    </div>   /* fim dashboard-layout */
  );
};

export default Dashboard;
